<template>
  <header
    class="backdrop-blur bg-surface-canvas/95 border-b border-border-level-200 dark:bg-surface-level-900/95 dark:border-border-level-700 sticky top-0 z-(--z-index-ds-sticky)"
  >
    <nav
      class="flex items-center justify-between lg:px-8 p-3"
      :aria-label="$t('common.accessibility.globalNavigation')"
    >
      <div class="flex lg:flex-1 max-[1140px]:lg:flex-none max-[1148px]:lg:px-4">
        <Logo
          :to="logoLink"
          wrapper-class="-m-1.5 p-1.5"
          height-class="h-10 sm:h-10"
          alt=""
        />
      </div>

      <div
        class="hidden lg:flex lg:flex-none lg:items-center lg:justify-center lg:space-x-6 max-[1148px]:lg:space-x-4"
      >
        <slot name="center" />
      </div>

      <div
        class="2xl:gap-x-8 flex gap-x-4 items-center lg:flex-1 lg:justify-end max-[1148px]:lg:space-x-4 text-center"
      >
        <slot name="preActions" />

        <div
          v-if="isAuthenticated && identity"
          class="hidden lg:block"
        >
          <UPopover
            :content="menuContent"
            :ui="menuUi"
          >
            <button
              type="button"
              class="cursor-pointer flex focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary focus:outline-none items-center outline-hidden relative rounded-full"
            >
              <span class="sr-only">
                {{ $t('common.accessibility.openUserMenu') }}
              </span>

              <img
                class="-outline-offset-1 bg-surface-level-50 dark:bg-surface-level-800 dark:outline-focus-inverse/10 object-cover outline outline-focus-ink/5 pointer-events-none rounded-full select-none size-8"
                :src="userAvatarSrc"
                :alt="$t('common.accessibility.userAvatar')"
              />
            </button>

            <template #content="{ close }">
              <div class="p-4">
                <NuxtLink
                  v-for="item in primaryMenus"
                  :key="item.key ?? item.route ?? item.label ?? 'primary-menu-item'"
                  :to="item.route || '#'"
                  class="dark:hover:bg-surface-canvas/5 flex gap-x-3 group hover:bg-surface-level-50 items-center justify-start p-3 relative rounded-lg text-left"
                  @click="close"
                >
                  <div
                    class="bg-surface-level-50 dark:bg-surface-level-700/50 dark:group-hover:bg-surface-level-700 flex flex-none group-hover:bg-surface-canvas items-center justify-center rounded-lg size-8"
                  >
                    <component
                      :is="item.icon"
                      class="dark:group-hover:text-content-inverse dark:text-content-level-400 group-hover:text-primary size-4 text-content-level-600"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="flex-1 min-w-0">
                    <span class="dark:text-content-inverse font-semibold text-content-level-900">
                      {{ item.label }}
                      <span class="absolute inset-0" />
                    </span>
                  </div>
                </NuxtLink>

                <NuxtLink
                  v-for="item in userMenus"
                  :key="item.key ?? item.route ?? item.label ?? 'user-menu-item'"
                  :to="item.route || '#'"
                  class="dark:hover:bg-surface-canvas/5 flex gap-x-3 group hover:bg-surface-level-50 items-center justify-start p-3 relative rounded-lg text-left"
                  @click="close"
                >
                  <div
                    class="bg-surface-level-50 dark:bg-surface-level-700/50 dark:group-hover:bg-surface-level-700 flex flex-none group-hover:bg-surface-canvas items-center justify-center rounded-lg size-8"
                  >
                    <component
                      :is="item.icon"
                      class="dark:group-hover:text-content-inverse dark:text-content-level-400 group-hover:text-primary size-4 text-content-level-600"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="flex-1 min-w-0">
                    <span class="dark:text-content-inverse font-semibold text-content-level-900">
                      {{ item.label }}
                      <span class="absolute inset-0" />
                    </span>
                  </div>
                </NuxtLink>

                <button
                  type="button"
                  class="appearance-none bg-surface-clear border-none cursor-pointer dark:hover:bg-surface-canvas/5 flex gap-x-3 group hover:bg-surface-level-50 items-center justify-start p-3 relative rounded-lg text-left w-full"
                  @click="
                    () => {
                      close()
                      doLogout()
                    }
                  "
                >
                  <div
                    class="bg-surface-level-50 dark:bg-surface-level-700/50 dark:group-hover:bg-surface-level-700 flex flex-none group-hover:bg-surface-canvas items-center justify-center rounded-lg size-8"
                  >
                    <ArrowRightOnRectangleIcon
                      class="dark:group-hover:text-danger-300 dark:text-danger-400 group-hover:text-danger-700 size-4 text-danger-600"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="flex-1 min-w-0">
                    <span class="dark:text-danger-400 font-semibold text-danger-600">{{
                      $t('common.actions.signOut')
                    }}</span>
                  </div>
                </button>
              </div>
            </template>
          </UPopover>
        </div>

        <div
          v-if="variant === 'landing' && !isAuthenticated"
          class="gap-8 hidden items-center lg:flex max-[1148px]:gap-4 max-[1148px]:ml-4 ml-4 mr-0"
        >
          <UPopover
            :content="menuContent"
            :ui="menuUi"
          >
            <button
              type="button"
              class="[&[aria-expanded='true']]:border-0 [&[aria-expanded='true']]:outline-none [&[aria-expanded='true']]:ring-0 [&[aria-expanded='true']]:ring-offset-0 bg-surface-clear border-0 cursor-pointer dark:hover:text-primary dark:text-content-inverse focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 font-semibold gap-x-1 hover:text-primary inline-flex items-center outline-none ring-0 ring-offset-0 rounded-md shadow-none text-content-level-900 text-sm/6"
            >
              <span>
                {{ $t('common.navigation.forRestaurants') }}
              </span>

              <ChevronDownIcon
                class="dark:text-content-level-400 size-5 text-content-level-500"
                aria-hidden="true"
              />
            </button>

            <template #content="{ close }">
              <div class="p-4">
                <NuxtLink
                  v-for="item in forRestaurantsSolutions"
                  :key="item.name"
                  :to="item.href"
                  class="dark:hover:bg-surface-canvas/5 flex gap-x-3 group hover:bg-surface-level-50 items-center justify-start p-3 relative rounded-lg text-left"
                  @click="close"
                >
                  <div
                    class="bg-surface-level-50 dark:bg-surface-level-700/50 dark:group-hover:bg-surface-level-700 flex flex-none group-hover:bg-surface-canvas items-center justify-center rounded-lg size-8"
                  >
                    <component
                      :is="item.icon"
                      class="dark:group-hover:text-primary dark:text-content-level-400 group-hover:text-primary size-4 text-content-level-600"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="flex-1 min-w-0">
                    <span class="dark:text-content-inverse font-semibold text-content-level-900">
                      {{ item.name }}
                      <span class="absolute inset-0" />
                    </span>
                  </div>
                </NuxtLink>
              </div>
            </template>
          </UPopover>

          <UPopover
            :content="menuContent"
            :ui="menuUi"
          >
            <button
              type="button"
              class="[&[aria-expanded='true']]:border-0 [&[aria-expanded='true']]:outline-none [&[aria-expanded='true']]:ring-0 [&[aria-expanded='true']]:ring-offset-0 bg-surface-clear border-0 cursor-pointer dark:hover:text-primary dark:text-content-inverse focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 font-semibold gap-x-1 hover:text-primary inline-flex items-center outline-none ring-0 ring-offset-0 rounded-md shadow-none text-content-level-900 text-sm/6"
            >
              <span>
                {{ $t('common.navigation.forCustomers') }}
              </span>

              <ChevronDownIcon
                class="dark:text-content-level-400 size-5 text-content-level-500"
                aria-hidden="true"
              />
            </button>

            <template #content="{ close }">
              <div class="p-4">
                <NuxtLink
                  v-for="item in forCustomersSolutions"
                  :key="item.name"
                  :to="item.href"
                  class="dark:hover:bg-surface-canvas/5 flex gap-x-3 group hover:bg-surface-level-50 items-center justify-start p-3 relative rounded-lg text-left"
                  @click="close"
                >
                  <div
                    class="bg-surface-level-50 dark:bg-surface-level-700/50 dark:group-hover:bg-surface-level-700 flex flex-none group-hover:bg-surface-canvas items-center justify-center rounded-lg size-8"
                  >
                    <component
                      :is="item.icon"
                      class="dark:group-hover:text-primary dark:text-content-level-400 group-hover:text-primary size-4 text-content-level-600"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="flex-1 min-w-0">
                    <span class="dark:text-content-inverse font-semibold text-content-level-900">
                      {{ item.name }}
                      <span class="absolute inset-0" />
                    </span>
                  </div>
                </NuxtLink>
              </div>
            </template>
          </UPopover>
        </div>

        <div
          v-if="variant !== 'landing' && !isAuthenticated"
          class="gap-8 hidden items-center lg:flex max-[1148px]:gap-4 max-[1148px]:ml-4 ml-4 mr-0"
        >
          <NuxtLink
            to="/register"
            class="dark:text-content-inverse font-semibold hover:text-primary text-content-level-900 text-sm/6"
          >
            {{ $t('common.actions.signUp') }}
          </NuxtLink>

          <NuxtLink
            to="/login"
            class="bg-primary dark:focus-visible:ring-offset-focus-level-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary font-semibold hover:bg-primary/90 inline-flex items-center justify-center px-4 py-2 rounded-lg shadow-sm text-content-inverse text-sm transition-colors"
          >
            {{ $t('common.actions.signIn') }}
          </NuxtLink>
        </div>

        <button
          type="button"
          class="-m-2.5 inline-flex items-center justify-center lg:hidden p-2.5 rounded-md text-base-content"
          @click="openMobileMenu"
        >
          <span class="sr-only">
            {{ $t('common.accessibility.openMainMenu') }}
          </span>

          <Bars3Icon
            class="size-6"
            aria-hidden="true"
          />
        </button>
      </div>
    </nav>

    <slot name="bottom" />
  </header>
</template>

<script setup lang="ts">
import UPopover from '@nuxt/ui/components/Popover.vue'
import { ArrowRightOnRectangleIcon, Bars3Icon } from '@heroicons/vue/24/outline'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { useNavigationSolutions } from '~/composables/useNavigationSolutions'
import { useNavAuthMenu } from '~/composables/useNavAuthMenu'
import { useLogoLink } from '~/composables/useLogoLink'
import { useLogger } from '~/composables/useLogger'
import { overlayContentClasses } from '~/utils/overlayContentClasses'

const props = withDefaults(
  defineProps<{
    /** Header presentation variant. */
    variant?: 'default' | 'checkout' | 'landing' | 'search'
    /** Notification reset invoked before logout; owned and passed down by the mounting navigation wrapper. */
    resetNotifications?: (() => void | Promise<void>) | undefined
  }>(),
  {
    resetNotifications: undefined,
    variant: 'default'
  }
)

const emit = defineEmits<{ openMobileMenu: [] }>()

const menuContent = { align: 'end', sideOffset: 12 } as const

const menuUi = {
  content: [overlayContentClasses.frame, overlayContentClasses.panel, 'text-sm/6', 'w-56'].join(' ')
} as const

const logger = useLogger('AppNavHeader')
const { logoLink } = useLogoLink()
const forRestaurantsSolutions = useNavigationSolutions('restaurants')
const forCustomersSolutions = useNavigationSolutions('customers')

const openMobileMenu = () => {
  emit('openMobileMenu')
}

defineSlots<{
  center?: () => unknown
  preActions?: () => unknown
  bottom?: () => unknown
}>()

const { identity, isAuthenticated } = useIdentity()

const { primaryMenus, userMenus, userAvatarSrc, handleLogout } = useNavAuthMenu()

const doLogout = () => {
  handleLogout(() => props.resetNotifications?.()).catch((logoutError: unknown) => {
    logger.error('doLogout', { logoutError })
  })
}
</script>
